import { revisionDisplayLabel, type RevisionInfo } from "src/api/records";

/** What a screening citation quoted, recorded by the server at commit. */
export interface CitationPin {
  sdr_id: number;
  chain_id?: number | null;
  supersedes_fk?: number | null;
  /** Set when the cited revision was already superseded when it was cited. */
  superseded_date?: string | null;
  revision_label?: string | null;
  revision_date?: string | null;
  chunks?: { id: number; data_hash?: string | null }[];
  /** Each complete match of the quote at cite time, as chunk ids in order. */
  windows?: number[][] | null;
  /** The record's content hash and version at cite time. */
  data_hash?: string | null;
  version_num?: number | null;
  quote_located?: boolean | null;
  pinned_at?: string;
}

export type CitationPinState =
  | "current"
  | "superseded"
  | "changed"
  | "unavailable";

/**
 * The cited revision's state, computed by the server from the record:
 * revisions are immutable, so the cited revision is the cited text, except
 * for a record edited in place (`changed`), whose content hash no longer
 * matches the pinned one.
 */
export interface CitationPinStatus {
  state: CitationPinState;
  /** Whether opening the citation still shows the cited revision. */
  cited_revision_available: boolean;
  /**
   * The chunk holding the quoted passage. Null when the quote was not located
   * when cited or its passage is gone, which opens the revision unhighlighted.
   */
  jump_chunk_id?: number | null;
  /**
   * Whether the whole quoted passage is still in the cited record's chunks;
   * false after a re-chunk changed or dropped part of it, null when there is
   * nothing to check. Informational only: it never changes `state`.
   */
  cited_text_present?: boolean | null;
  current_revision_sdr_id?: number | null;
  current_revision_label?: string | null;
}

export interface PinnedReference {
  pin?: CitationPin | null;
  pin_status?: CitationPinStatus | null;
}

/**
 * The cited revision as a reviewer reads it, matching the library's revision
 * labels: the publish label ("Rev 3"), else the revision's date, else its
 * record id. Null for a citation made before pins were recorded.
 */
export const citedRevisionLabel = (
  pin: CitationPin | null | undefined,
): string | null => {
  if (!pin) return null;
  const revision: RevisionInfo = {
    id: pin.sdr_id,
    chain_id: pin.chain_id ?? null,
    revision_label: pin.revision_label ?? null,
    supersedes_fk: pin.supersedes_fk ?? null,
    superseded_date: pin.superseded_date ?? null,
    revision_source: null,
    create_date: pin.revision_date ?? null,
  };
  return revisionDisplayLabel(revision);
};

/** True when the cited record was deleted, so it can no longer be opened. */
export const citedRevisionUnavailable = (reference: PinnedReference): boolean =>
  reference.pin_status?.state === "unavailable";

/**
 * True when the cited record was edited in place since it was cited: it still
 * opens, but what it shows is no longer the cited content.
 */
export const citedRevisionChanged = (reference: PinnedReference): boolean =>
  reference.pin_status?.state === "changed";

/** True when the cited record is still there but its quoted passage is not. */
export const citedPassageMissing = (reference: PinnedReference): boolean =>
  reference.pin_status?.cited_text_present === false &&
  reference.pin_status.cited_revision_available;
